import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "selectable", "server-side", "total-items", "current-page", "loading"]

import { computed, onMounted, onUnmounted, ref, watchEffect, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import { useRouter } from 'vue-router';

import { EPINIO_TYPES, EPINIO_SERVICE_PARAM } from '../types';
import { startPolling, stopPolling } from '../utils/polling';
import Masthead from '@shell/components/ResourceList/Masthead';
import { makeStateTag, makeRouterLink, makeNameLinks, makeActionMenu } from '../utils/table-formatters';
import EpinioServiceModel from 'models/services';
import { overrideTableRows } from '../utils/table-formatters';
import ServiceDeleteModal from '../components/service/ServiceDeleteModal.vue';
import ServiceInstanceModal from '../components/service/ServiceInstanceModal.vue';
import BulkDeleteModal from '../components/BulkDeleteModal.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'services',
  props: {
    schema: {}
  },
  setup(__props: any) {



const store = useStore() as any;
const t = store.getters['i18n/t'];
const router = useRouter();

const resource: string = EPINIO_TYPES.SERVICE_INSTANCE;
const paginationMeta = computed(() => store.getters['epinio/paginationMeta'](resource));
const currentPage = computed(() => store.getters['epinio/currentPaginationPage'](resource));

const searchQuery = ref<string>('');

const paginating = ref(false);

async function goToPage(page: number) {
  const meta = paginationMeta.value;

  if (meta && (page < 1 || page > meta.totalPages)) return;
  paginating.value = true;
  try {
    await store.dispatch('epinio/goToPage', { type: resource, page });
  } finally {
    paginating.value = false;
  }
}

const onSearch = debounce(async (query: string) => {
  paginating.value = true;
  try {
    await store.dispatch('epinio/search', { type: resource, query });
  } finally {
    paginating.value = false;
  }
}, 500);

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const serviceModal = ref<InstanceType<typeof ServiceInstanceModal> | null>(null);
const deleteModal = ref<InstanceType<typeof ServiceDeleteModal> | null>(null);
const bulkDeleteModal = ref<InstanceType<typeof BulkDeleteModal> | null>(null);
const tableEl = ref<any>(null);
const selectedRows = ref<any[]>([]);
const displayRows = ref<any[]>([]);

const canEdit = computed(() => {
  const can = store.getters['epinio/can'];

  return can && (can('service_write') || can('service'));
});
const canDelete = canEdit;
const canCreate = canEdit;

watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;
  const all = store.getters['epinio/all'](EPINIO_TYPES.SERVICE_INSTANCE) as any[];
  all.forEach((row: any) => { void row.status; void row.stateDisplay; void row.meta; void row.boundapps; });

  // Filter empty rows that are added during delete, and filter by active namespace
  const filtered = all.filter((row) => {
    if (!row.id) return false;
    const ns = row.meta?.namespace;

    return !activeNamespaces || Object.keys(activeNamespaces).length === 0 || activeNamespaces[ns];
  });

  // Build the row action menu with RBAC gating. The model already gates the
  // base actions; here we inject the modal-driven Edit/Delete entries only
  // when the user has service write permissions.
  const rowActions = (row: EpinioServiceModel) => {
    const out: any[] = [];

    if (canEdit.value) {
      out.push({
        action: 'editServiceModal',
        label: 'Edit',
        enabled: true
      });
    }
    if (canDelete.value) {
      out.push({
        action: 'removeService',
        altAction: 'remove',
        bulkAction: 'removeService',
        bulkable: true,
        enabled: row.canDelete,
        icon: 'icon icon-trash',
        label: 'Delete',
        weight: -10
      });
    }


    return out;
  };

  const overrideProps = [
    {
      prop: 'availableActions',
      value: rowActions,
      conditionFn: () => true,
    },
    {
      prop: 'removeService',
      value: (row: EpinioServiceModel) => () => {
        deleteModal.value?.openDelete(row);
      },
      conditionFn: (row: EpinioServiceModel) => canDelete.value && row.canDelete,
    },
    {
      prop: 'editServiceModal',
      value: (row: EpinioServiceModel) => () => {
        serviceModal.value?.openEdit(row);
      },
      conditionFn: () => canEdit.value,
    }
  ];

  const processedRows = overrideTableRows(filtered, overrideProps);

  displayRows.value = [...processedRows];
});

onMounted(async () => {
  paginating.value = true;
  try {
    await Promise.all([
      store.dispatch('epinio/me'),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE }),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE }),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CATALOG_SERVICE }),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP }),
    ]);
  } finally {
    paginating.value = false;
  }
  startPolling(['services', 'applications', 'namespaces'], store);

  const query = store.$router.currentRoute._value.query;

  if (query.mode === 'openModal') {
    serviceModal.value?.openCreate(query[EPINIO_SERVICE_PARAM] as string | undefined);
  }
});

onUnmounted(() => {
  stopPolling(['services', 'applications', 'namespaces']);
});

// Services without service_write/service permission on that row can't be
// individually deleted, so they're excluded from bulk selection too.
const isRowSelectable = (row: any) => row.canDelete;

const handleSelectionChange = (event: CustomEvent) => {
  selectedRows.value = event.detail.selectedRows;
};

const handleBulkDeleteClick = () => {
  bulkDeleteModal.value?.openDelete(selectedRows.value);
};

const handleBulkDeleted = () => {
  selectedRows.value = [];
  tableEl.value?.clearSelection();
};

// NOTE: must be a named function declared here, not an inline arrow function
// in the template, see feedback_vue_ref_unwrap_bug.md. Vue auto-unwraps
// top-level refs inside template expressions, so referencing `tableEl`
// directly in an inline template callback gives the already-unwrapped
// (initially null) value instead of the Ref object, and `tableEl.value = el`
// throws, silently aborting the rest of the block.
const setTableRef = (el: any) => {
  if (el) {
    tableEl.value = el;
    el.renderActions = makeActionMenu;
    el.rowSelectable = isRowSelectable;
  }
};

const handleNavigate = (event: CustomEvent) => {
  router.push(event.detail.url);
};

const columns = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px',
    formatter: (_v: any, row: any) => makeStateTag(row)
  },
  {
    field: 'nameDisplay',
    label: 'Name',
    formatter: (_v: any, row: any) => {
      const el = document.createElement('a');

      el.textContent = row.nameDisplay || row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        serviceModal.value?.openView(row);
      });

      return el;
    }
  },
  {
    field: 'namespace',
    label: 'Namespace'
  },
  {
    field: 'catalog_service',
    label: 'Catalog Service',
    sortable: false,
    formatter: (_v: any, row: any) => makeRouterLink(row.catalog_service, row.serviceLocation, router)
  },
  {
    field: 'catalog_service_version',
    label: 'Catalog Service Version'
  },
  {
    field: 'boundApps',
    label: 'Bound Applications',
    sortable: false,
    formatter: (_v: any, row: any) => makeNameLinks(
      row.boundapps,
      { cluster: store.getters['clusterId'], namespace: row.meta?.namespace, resource: EPINIO_TYPES.APP },
      router
    )
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.schema,
      resource: _unref(resource)
    }, {
      extraActions: _withCtx(() => [
        (selectedRows.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "destructive",
              size: "large",
              onClick: handleBulkDeleteClick
            }, [
              _cache[3] || (_cache[3] = _createElementVNode("trailhand-icon", { name: "trash" }, null, -1)),
              _createTextVNode("\n          Delete (" + _toDisplayString(selectedRows.value.length) + ")\n        ", 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      createButton: _withCtx(() => [
        (_unref(canCreate))
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "primary",
              size: "large",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (serviceModal.value.openCreate()))
            }, _toDisplayString(_unref(t)('generic.create')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_2))
      ]),
      _: 1,
      __: [4]
    }, 8, ["schema", "resource"]),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("trailhand-text-input", {
        value: searchQuery.value,
        placeholder: "Search...",
        onTextInputChange: _cache[1] || (_cache[1] = (e) => searchQuery.value = e.detail.value)
      }, null, 40, _hoisted_4)
    ]),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: setTableRef,
      rows: displayRows.value,
      columns: columns,
      searchable: false,
      selectable: _unref(canDelete),
      "server-side": !!paginationMeta.value,
      "total-items": paginationMeta.value?.totalItems ?? displayRows.value.length,
      "current-page": currentPage.value,
      loading: paginating.value,
      "key-field": "id",
      onNavigate: handleNavigate,
      onPageChange: _cache[2] || (_cache[2] = (e) => goToPage(e.detail.page)),
      onSelectionChange: handleSelectionChange
    }, null, 40, _hoisted_5),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(ServiceInstanceModal, {
      ref_key: "serviceModal",
      ref: serviceModal
    }, null, 512),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createVNode(ServiceDeleteModal, {
      ref_key: "deleteModal",
      ref: deleteModal
    }, null, 512),
    _cache[9] || (_cache[9] = _createTextVNode()),
    _createVNode(BulkDeleteModal, {
      ref_key: "bulkDeleteModal",
      ref: bulkDeleteModal,
      "resource-label": "service instance",
      "resource-type": _unref(resource),
      "show-unbind-notice": true,
      onSettled: handleBulkDeleted
    }, null, 8, ["resource-type"])
  ]))
}
}

})